import Webcam from "react-webcam";
import { useCallback, useRef, useState, useEffect } from "react";
import { Modal } from "antd";
import Button from "../buttons/Buttonv1";
import ButtonWithIcon from "../buttons/ButtonWithIcon";
import { compressImageWithCanvas } from "../../../shared/utils/imageHelpers";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { HtmlButton, HtmlDocumentModal, DocumentViewerControls } from "../Html";
import {
  base64Size,
  compressBase64ToSize,
  fieldContainsValue,
  fileToBase64WithInfo,
  getFileURL,
  compressImageToMaxSize,
} from "../../../controllers/common";
import { DocumentPreview, HtmlAlertModal } from "../Modals";
import { takeReadAbelError } from "../../../controllers/readAbleError";
import { addJob } from "../../../services/jobManager/JobService";
import { SvgIcon } from "../Svg";
// { imgSrc, setImgSrc, height, width, mirrored, screenshotFormat, screenshotQuality }
const shutterSoundSrc =
  "data:audio/mp3;base64,SUQzAwAAAAAAF1RTU0MAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxAADB0NAgQAAQAAAwAADAARkwAA" +
  "AAAAFBQUEBQUFgAVAAABcAAAAEAAAAYAABAAFCRAABAEBAAAQAIAAQAAEBCAAQABAAEAwAEQAIAAQIBAAQAAQACAAAAAAQAAQABIiAg" +
  "AgAAAA==";
const WebCamera = ({ on = false, onCapture = () => {}, click = false, deviceId }) => {
  const [shutterVisible, setShutterVisible] = useState(false);
  const camRef = useRef(null);
  const camHeight = 1000;
  const [videoConstraints, setVideoConstraints] = useState({
    width: 1280,
    height: 720,
    facingMode: "user",
  });
  const capture = () => {
    if (camRef.current) {
      const imageSrc = camRef.current.getScreenshot();
      if (onCapture) {
        onCapture(imageSrc); // Pass the captured image to parent
      }
      setShutterVisible(true);
      setTimeout(() => setShutterVisible(false), 300);
    }
  };
  useEffect(() => {
    if (click) {
      capture();
    }
    if (deviceId) {
      setVideoConstraints((prev) => ({
        ...prev,
        deviceId: { exact: deviceId },
      }));
    }
  }, [click, deviceId]);

  const playShutterSound = () => {
    const shutterSound = new Audio(shutterSoundSrc);
    shutterSound.play();
  };

  return (
    <>
      {on && (
        <div style={{ position: "relative", height: camHeight, width: "100vw" }}>
          <Webcam
            audio={false}
            ref={camRef}
            screenshotFormat="image/png"
            screenshotQuality={1}
            height={camHeight}
            videoConstraints={{ ...videoConstraints }}
            mirrored={false}
            style={{
              height: "100%",
              maxHeight: "100vh",
              width: "100vw",
              objectFit: "contain",
              backgroundColor: "black",
            }}
          />
          {shutterVisible && <div className="shutter" />}
        </div>
      )}
    </>
  );
};
const ImageEditor = ({ image, onCancel = () => {}, onEdit = () => {} }) => {
  const [img, setImg] = useState(image);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 95,
    height: 95,
    y: 2.5,
    x: 2.5,
    aspect: undefined,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [isCheckingType, setIsCheckingType] = useState(true);

  useEffect(() => {
    setImg(image);
    setCrop({
      unit: "%",
      width: 95,
      height: 95,
      y: 2.5,
      x: 2.5,
      aspect: undefined,
    });

    // Check if the image is actually a PDF
    checkIfPdf(image);
  }, [image]);

  const checkIfPdf = async (url) => {
    if (!url) {
      setIsPdf(false);
      setIsCheckingType(false);
      return;
    }

    // Check if it's a base64 PDF
    if (url.startsWith("data:application/pdf;base64,")) {
      setIsPdf(true);
      setIsCheckingType(false);
      return;
    }

    // Check if URL contains .pdf extension
    if (url.toLowerCase().includes(".pdf")) {
      setIsPdf(true);
      setIsCheckingType(false);
      return;
    }

    // For blob URLs or other unknown types, try to fetch and check the content type
    if (url.startsWith("blob:")) {
      try {
        const response = await fetch(url, { method: "HEAD" });
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("pdf")) {
          setIsPdf(true);
        } else {
          setIsPdf(false);
        }
      } catch (e) {
        console.warn("Could not determine file type, assuming image");
        setIsPdf(false);
      }
      setIsCheckingType(false);
      return;
    }

    // Default to image
    setIsPdf(false);
    setIsCheckingType(false);
  };

  const onImageLoaded = (img) => {
    setImageRef(img);
  };

  const getCroppedImg = () => {
    if (!imageRef || !completedCrop?.width || !completedCrop?.height) return;
    const canvas = document.createElement("canvas");
    const scaleX = imageRef.naturalWidth / imageRef.width;
    const scaleY = imageRef.naturalHeight / imageRef.height;
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      imageRef,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );
    // canvas.toBlob((blob) => {
    //   if (!blob) return;
    //   const croppedUrl = URL.createObjectURL(blob);
    //   onEdit(croppedUrl);
    // }, "image/jpeg");
    const base64Image = canvas.toDataURL("image/jpeg");
    onEdit(base64Image);
  };

  return (
    <div
      style={{
        width: "100dvw",
        height: "100vh",
        backgroundColor: "black",
        top: 0,
        left: 0,
        zIndex: 1100,
        position: "fixed",
        display: img ? "block" : "none",
      }}
    >
      <div
        style={{ height: "8vh" }}
        className="flex justify-between items-center px-5 text-[17px] py-1 text-white"
      >
        <button
          onClick={() => {
            setImg(null);
            onCancel();
          }}
        >
          Cancel
        </button>
        {!isPdf && <button onClick={getCroppedImg}>Done</button>}
        {isPdf && (
          <button
            onClick={() => {
              // For PDFs, just close the editor without editing
              onEdit(img);
            }}
          >
            Done
          </button>
        )}
      </div>

      <div style={{ position: "relative", width: "100%", height: "92vh" }} className="">
        <div
          className="flex justify-center items-center "
          style={{
            maxWidth: "90%",
            minWidth: "80%",
            maxHeight: "90%",
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            paddingTop: "5%",
            // paddingBottom:'5%',
          }}
        >
          {isCheckingType && <div className="text-white">Loading...</div>}
          {!isCheckingType && !isPdf && (
            <ReactCrop
              src={img}
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              onImageLoaded={onImageLoaded}
              keepSelection={true}
              // disabled={true}
              // locked={true}
              // maxHeight="90%"
              // maxWidth= "90%"
              imageStyle={{
                maxHeight: "80vh",
                maxWidth: "80vw",
                width: "auto",
                height: "auto",
                margin: "auto",
              }}
              // style={{
              //   maxWidth: "100%",
              //   maxHeight: "100%",
              //   width: "auto",
              //   height: "auto",
              //   objectFit: "contain",
              //   overflow: "visible" ,
              //   display: "block",
              // }}
              className="w-[auto] h-[auto]"
            />
          )}
          {!isCheckingType && isPdf && (
            <div
              className="md:-mt-20 -mt-8"
              style={{
                width: "96vw",
                height: "90vh",
              }}
            >
              <DocumentViewerControls
                billUrl={img}
                btnAllowed={["zoomIn", "zoomOut", "rotate", "reset"]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const MultiImageCaptureCamera = ({
  open = false,
  onClose = () => {},
  onSelect = () => {},
  inputFileType = "png,jpeg,jpg",
  multiple = false,
  maxSelect = null,
  note = () => {},
  maxFileSize = 50,
}) => {
  const [openCamera, setOpenCamera] = useState(open);
  const [imageFiles, setImageFiles] = useState([]);
  const [click, setClick] = useState(false);
  const [editedImage, setEditedImage] = useState(null);
  const fileInput = useRef(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [videoPermission, setVideoPermission] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const compress_image_file = true;
  const clickImage = () => {
    setClick(true);
    setTimeout(() => {
      setClick(false);
    }, 10);
  };
  useEffect(() => {
    navigator.permissions.query({ name: "camera" }).then((permissionStatus) => {
      setVideoPermission(permissionStatus.state);
      permissionStatus.onchange = () => {
        setVideoPermission(permissionStatus.state);
      };
    });
  }, []);
  useEffect(() => {
    setOpenCamera(open);
    setImageFiles([]);
    setEditedImage(null);
  }, [open]);
  const onImageCapture = async (imageBuffer) => {
    let buffer = imageBuffer;
    const MAX_SIZE = 1 * 1024 * 1024;
    try {
      const sizeInBytes = base64Size(imageBuffer);
      if (sizeInBytes > MAX_SIZE) {
        buffer = await compressBase64ToSize(buffer, MAX_SIZE);
      }
      setImageFiles((prev) => [buffer, ...prev]);
    } catch (error) {
      alert("Please retry to click.");
    }
  };
  const removeImage = (indexToRemove) => {
    const newImagesList = imageFiles.filter((_, index) => index !== indexToRemove);
    setImageFiles(newImagesList);
  };
  const onEditImage = (imageFile) => {
    if (editedImage?.index !== undefined) {
      const newImagesList = [...imageFiles];
      newImagesList[editedImage?.index] = imageFile;
      setImageFiles(newImagesList);
    }
    setEditedImage(null);
  };
  const openGallery = () => {
    fileInput.current.click();
  };
  const handleFileSelected = async (e) => {
    let files = Array.from(e.target.files);
    if (compress_image_file && files && Array.isArray(files) && files.length > 0) {
      files = await Promise.all(
        files.map(async (file) => {
          // return await compressImageWithCanvas(file);
          // ✅ Compress only if it's an image
          if (file.type.startsWith("image/")) {
            return await compressImageWithCanvas(file);
          }
          // return file as-is if it's not an image
          return file;
        })
      );
    }
    const fileReaders = files.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(fileReaders).then((base64Files) => {
      setImageFiles((prev) => [...base64Files, ...prev]);
    });
  };
  const base64ArrayToFileArray = (base64Array) => {
    if (!Array?.isArray(base64Array)) return [];
    try {
      return base64Array?.map((base64, index) => {
        if (typeof base64 !== "string" || !base64.includes(",")) {
          throw new Error("Invalid base64 input");
          // alert("Please wait")
        }
        const [meta, content] = base64?.split(",");
        const mimeMatch = meta.match(/data:(.*);base64/);

        if (!mimeMatch) {
          throw new Error("Invalid base64 format");
        }

        const mime = mimeMatch[1];
        const byteString = atob(content);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const intArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
          intArray[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([intArray], { type: mime });

        const extension = mime.split("/")[1] || "file";
        const filename = `file_${index}.${extension}`;

        return new File([blob], filename, { type: mime });
      });
    } catch (error) {
      alert("Please retry to click");
    }
  };

  useEffect(() => {
    if (videoPermission === "granted") {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const videoDevices = devices.filter((device) => device.kind === "videoinput");
        setVideoDevices(videoDevices);
        const backCamera = videoDevices.find(
          (device) =>
            device.label.toLowerCase().includes("back") ||
            device.label.toLowerCase().includes("rear")
        );

        if (backCamera) {
          // setting by default back camera
          // You can store it in state if needed
          setSelectedDevice(backCamera.deviceId);
        }
      });
    }
  }, [videoPermission]);
  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
    // setVideContraints((prevConstraints) => ({
    //   ...prevConstraints,
    //   deviceId: { exact: event.target.value },
    // }));
  };
  return (
    <>
      <ImageEditor
        onCancel={() => {
          setEditedImage(null);
        }}
        image={editedImage?.image}
        onEdit={onEditImage}
      />
      <div
        style={{
          width: "100dvw",
          height: "100dvh",
          maxHeight: "100dvh",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1000,
          display: openCamera ? "block" : "none",
        }}
        className="bg-black"
      >
        <div
          className="bg-red-500 flex  items-center absolute"
          style={{
            top: 0,
            zIndex: 1,
            width: "100%",
            backgroundColor: "transparent",
            justifyContent: "space-between",
          }}
        >
          <select
            className="text-sm text-white w-24 h-10 p-2 bg-transparent border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedDevice}
            onChange={handleDeviceChange}
          >
            {videoDevices.map((device) => (
              <option className="" key={device.deviceId} value={device.deviceId}>
                {device.label.split()[0]}
              </option>
            ))}
          </select>
          {openCamera && (
            <div className="text-white">{note({ maxSelect, fileNumber: imageFiles?.length })}</div>
          )}
          <HtmlButton
            onClick={() => {
              setOpenCamera(false);
              onClose();
              setImageFiles([]);
            }}
            svgSize={30}
            svgType={"close"}
            style={{
              backgroundColor: "transparent",
              border: "none",
              color: "red",
            }}
            type={"default"}
          />
        </div>
        <div style={{ height: "100dvh" }} id="camera-container">
          <WebCamera
            deviceId={selectedDevice}
            on={openCamera}
            click={click}
            onCapture={onImageCapture}
          />
        </div>
        <div
          className="flex items-end gap-5 cursor-pointer invisible-scrollbar pb-1 pt-1"
          style={{
            width: "99%",
            height: "70px",
            position: "absolute",
            top: "79dvh",
            overflow: "scroll",
            marginLeft: "8px",
            marginRight: "8px",
            paddingLeft: "10px",
          }}
        >
          {imageFiles?.map((item, index) => {
            const isPdfFile =
              item.toLowerCase().includes(".pdf") || item.startsWith("data:application/pdf");
            return (
              <div
                key={index}
                className="relative flex-shrink-0"
                style={{ width: "50px", minWidth: "50px", height: "50px" }}
                onClick={() => {
                  setEditedImage({ image: item, index: index });
                }}
              >
                <span className="absolute -top-2 -left-2 w-5 h-5 bg-red-600 text-white text-xs font-semibold flex items-center justify-center rounded-full shadow">
                  {imageFiles?.length - index}
                </span>
                <span
                  className="absolute -top-2 -right-2 w-5 h-5 bg-white text-black text-sm flex items-center justify-center rounded-full shadow cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent parent click
                    removeImage(index);
                  }}
                >
                  ×
                </span>
                <div className="bg-black rounded-md overflow-hidden w-full h-full border border-white flex items-center justify-center">
                  {isPdfFile ? (
                    <div className="text-white text-[10px] font-bold">PDF</div>
                  ) : (
                    <img src={item} className="w-full h-full object-cover" alt="" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div
          id="camera-controllers-container"
          className="  items-center gap-10 absolute"
          style={{
            height: "11dvh",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            color: "white",
            paddingBottom: "7px",
            // top:0,
            bottom: 0,
            width: "100dvw",
          }}
        >
          <div className=" flex items-center">
            <div className=" flex flex-col items-center">
              <HtmlButton
                onClick={(e) => {
                  if (
                    (!multiple && imageFiles?.length > 0) ||
                    (maxSelect && maxSelect <= imageFiles?.length)
                  ) {
                    // e.preventDefault()
                  } else {
                    openGallery(e);
                  }
                }}
                svgSize={52}
                svgType={"gallery-icon"}
                className=""
                // disabled={!multiple && imageFiles?.length > 0 || (maxSelect && maxSelect<=imageFiles?.length)? true : false}
              />
              <span className="text-[10px]">Gallery</span>
            </div>
          </div>
          <div>
            <button
              style={{
                width: "50px",
                height: "50px",
                border: "solid",
                borderRadius: "500%",
                borderWidth: "4px",
                padding: "4px",
              }}
              disabled={
                (!multiple && imageFiles?.length > 0) ||
                (maxSelect && maxSelect <= imageFiles?.length)
                  ? true
                  : false
              }
              onClick={clickImage}
            >
              <p
                style={{
                  backgroundColor: "white",
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                }}
              >
                .
              </p>
            </button>
          </div>
          <div className=" flex items-center">
            <div className=" flex items-center flex-col">
              <button
                onClick={() => {
                  onSelect(base64ArrayToFileArray(imageFiles));
                }}
                disabled={imageFiles?.length <= 0}
                style={{ color: imageFiles?.length <= 0 ? "grey" : "white" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width={52} height={52} viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M13 17h4v-3l5 4.5l-5 4.5v-3h-4zm1-4.2c-.5-.49-1.22-.8-2-.8a2.996 2.996 0 0 0-1 5.82a6.03 6.03 0 0 1 3-5.02M11.09 19H5V5h11.17L19 7.83v4.52c.75.26 1.42.65 2 1.19V7l-4-4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6.81c-.35-.61-.6-1.28-.72-2M6 10h9V6H6z"
                  />
                </svg>
              </button>
              <span className="text-[10px]">Next</span>
            </div>
          </div>
        </div>

        <input
          type="file"
          accept={inputFileType
            .split(",")
            .map((ext) => "." + ext.trim())
            .join(",")}
          className="hidden"
          ref={fileInput}
          onChange={(e) => {
            const allowedExtensions = inputFileType.split(",").map((ext) => "." + ext.trim());
            const MAX_FILE_SIZE = maxFileSize * 1024 * 1024;
            const selectedFiles = Array.from(e.target.files);
            const invalid = selectedFiles.filter(
              (file) =>
                !allowedExtensions.some((ext) =>
                  file.type.toLowerCase().endsWith(ext?.replace(".", ""))
                )
            );
            const tooLarge = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE);
            if (invalid.length > 0) {
              const types = inputFileType?.replaceAll(",", ", ");
              alert("Only " + types + " files are allowed.");
              e.target.value = "";
              return;
            }
            if (tooLarge.length > 0) {
              alert(`Each file must be less than ${maxFileSize}MB.`);
              e.target.value = "";
              return;
            }
            if (selectedFiles.length > maxSelect - imageFiles?.length) {
              alert(`You can select up to ${maxSelect - imageFiles.length} files only.`);
              e.target.value = ""; // Clear the selection
              // return;
              e.preventDefault();
            } else {
              handleFileSelected(e);
              e.target.value = "";
            }
          }}
          multiple={multiple}
        />
      </div>
    </>
  );
};
const DocumentUploadHandler = ({
  value,
  uploadBtnTitle = "Upload",
  previewBtnTitle = "Open",
  uploadMethod = "default",
  uploadFunction,
  docType,
  afterUpload = () => {},
  onSelect = () => {},
  downloadable = false,
  addMore = false,
  jobData = {},
  inputFileType = "png,jpeg,jpg",
  multiple = false,
  buttons = true,
  camera,
  onCameraClose = () => {},
  maxSelect = null,
  note = () => {},
}) => {
  const [view, setView] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [htmlAlertModal, setHtmlAlertModal] = useState({
    okDisabled: false,
    cancelDisabled: false,
    okLoading: false,
    loading: false,
    title: "",
    message: "",
    cancelText: "",
    okText: "",
    okAction: () => {},
    cancelAction: () => {},
    open: false,
    close: false,
    maskClosable: false,
    alertType: "",
  });
  const closeHtmlAlertModal = () => {
    setHtmlAlertModal({
      okDisabled: false,
      cancelDisabled: false,
      okLoading: false,
      loading: false,
      title: "",
      message: "",
      cancelText: "",
      okText: "",
      okAction: () => {},
      cancelAction: () => {},
      open: false,
      close: false,
      maskClosable: false,
      alertType: "",
    });
  };
  useEffect(() => {
    setOpenCamera(camera);
  }, [camera]);
  const onImagesSelect = async (files) => {
    setOpenCamera(false);
    if (uploadMethod === "default") {
      setOpenCamera(false);
      defaultMethodDocumentUpload({ files });
    } else if (uploadMethod === "return") {
      setOpenCamera(false);
      onSelect(files);
    } else if (uploadMethod === "job") {
      setOpenCamera(false);

      const arrayUrls = [];
      // Process files sequentially to prevent memory overload
      for (let i = 0; i < files?.length; i++) {
        const tempUrl = getFileURL(files[i]);
        arrayUrls.push({ url: tempUrl });

        // Compress image before converting to base64 to reduce memory usage
        let fileUrl;
        if (files[i]?.type?.startsWith("image/")) {
          try {
            fileUrl = await compressImageToMaxSize(files[i], 4); // Max 1MB
          } catch (compressionError) {
            // Fallback to original file if compression fails
            console.warn("Image compression failed, using original:", compressionError);
            fileUrl = await fileToBase64WithInfo(files[i]);
          }
        } else {
          fileUrl = await fileToBase64WithInfo(files[i]);
        }

        const jobMetadata = {
          ...jobData,
          data: { ...jobData?.data, file: fileUrl, fileType: files[i]?.type },
        };
        if (files?.length > 1) {
          jobMetadata["metaData"] = { ...jobData?.metaData, fileIndex: i + 1 };
        }
        addJob(jobMetadata);
      }
      afterUpload(arrayUrls);
    }
  };
  const defaultMethodDocumentUpload = async ({ files }) => {
    let message = "";
    let alertType = "";
    let title = "";
    setHtmlAlertModal((prev) => ({
      ...prev,
      open: true,
      loading: true,
      title: "Uploading",
    }));
    let index = 0;
    try {
      if (!fieldContainsValue(docType)) {
        throw "docType is required.";
      }
      let result;
      let response;
      if (files?.length > 1) {
        const results = [];
        for (let i = 0; i < files?.length; i++) {
          index = i;
          response = await uploadFunction(docType, files[i]);
          results[i] = response?.results;
          setHtmlAlertModal((prev) => ({
            ...prev,
            message: `File ${i + 1} uploaded`,
          }));
        }
        result = results;
      } else {
        response = await uploadFunction(docType, files[0]);
        result = response?.results;
      }
      if (response?.status === "success") {
        title = "Upload complete.";
        alertType = "success";
        message = `${files?.length} Files uploaded`;
        afterUpload(result);
      } else {
        title = "Unable to upload.";
        alertType = "error";
        message = "";
      }
    } catch (error) {
      message = takeReadAbelError(error);
      title = "Unable to upload.";
      alertType = "error";
    }
    setHtmlAlertModal((prev) => ({
      ...prev,
      open: true,
      loading: false,
      title: title,
      message: message,
      alertType: alertType,
    }));
  };
  const onAddMore = () => {
    setView(null);
    setOpenCamera(true);
  };
  return (
    <>
      <HtmlAlertModal
        okDisabled={htmlAlertModal?.okDisabled}
        cancelDisabled={htmlAlertModal?.cancelDisabled}
        okLoading={htmlAlertModal?.okLoading}
        loading={htmlAlertModal?.loading}
        title={htmlAlertModal?.title}
        message={htmlAlertModal?.message}
        cancelText={htmlAlertModal?.cancelText}
        okText={htmlAlertModal?.okText}
        okAction={htmlAlertModal?.okAction}
        cancelAction={htmlAlertModal?.cancelAction}
        open={htmlAlertModal?.open}
        close={closeHtmlAlertModal}
        maskClosable={htmlAlertModal?.maskClosable}
        alertType={htmlAlertModal?.alertType}
      />
      <HtmlDocumentModal
        text={view}
        download={downloadable}
        addMore={addMore}
        imageValue={view}
        onAddMore={onAddMore}
        onClose={() => {
          setView(null);
        }}
      />
      <MultiImageCaptureCamera
        onSelect={onImagesSelect}
        open={openCamera}
        onClose={() => {
          setOpenCamera(false);
          onCameraClose(false);
        }}
        inputFileType={inputFileType}
        multiple={multiple}
        maxSelect={maxSelect}
        note={note}
      />
      {fieldContainsValue(value) ? (
        <div style={{ display: buttons ? "block" : "none" }}>
          <HtmlButton
            onClick={() => {
              setView(value);
            }}
            svgSize={25}
            svgType={"upload-done"}
            className="underline"
            style={{
              border: "none",
              backgroundColor: "transparent",
              color: "rgb(52,199,89)",
              fontSize: "15px",
              textDecoration: "underline",
            }}
            type={"default"}
            text={previewBtnTitle}
          />
        </div>
      ) : (
        <div style={{ display: buttons ? "block" : "none" }}>
          <HtmlButton
            svgType={"upload"}
            type={"primary"}
            outline={true}
            text={uploadBtnTitle}
            onClick={() => {
              setOpenCamera(true);
            }}
          />
        </div>
      )}
    </>
  );
};
const WebCameraModal = ({}) => {
  const [openCamera, setOpenCamera] = useState(false);
  const [click, setClick] = useState(false);
  const clickImage = () => {
    setClick(true);
    setTimeout(() => {
      setClick(false);
    }, 10);
  };
  const onImageCapture = (imageBuffer) => {};
  return (
    <>
      <div
        style={{
          width: "100dvw",
          height: "100vh",
          maxHeight: "100dvh",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1000,
        }}
        className="bg-red-500"
      >
        <div style={{ height: "90dvh" }}>
          <WebCamera on={openCamera} click={click} onCapture={onImageCapture} />
        </div>
        <div style={{ zIndex: 1100 }} className="absolute">
          <button
            onClick={() => {
              setOpenCamera(true);
            }}
          >
            Open
          </button>
          <button
            onClick={() => {
              setOpenCamera(false);
            }}
          >
            Close
          </button>
          <button onClick={clickImage}>CLick</button>
        </div>
      </div>
    </>
  );
};
export const ImageCaptureButton = ({
  downloadBtnDisabled = true,
  addAnotherDisable = true,
  viewButtonDisabled = true,
  disabled,
  disabledMessage = "",
  docViewButtonStyle = {},
  docViewButtonSvgSize = 24,
  docViewButtonType = "detail-page",
  docViewButtonSvgType = "upload-done",
  docViewButtonText = "View Image",
  value,
  title,
  mandatory,
  text = "Upload Image",
  buttonType = "detail-page",
  svgType = "upload-image",
  onCameraClose = () => {},
  onCameraOpen = () => {},
  fileType = "png,jpeg,jpg,pdf",
  allowMultiple = false,
  numberOfImagesAllowed = 1,
  maxFileSize = 50,
  note = () => {},
  onImageSelect = () => {},
}) => {
  const [isCameraOpen, setIsOpenCamera] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  useEffect(() => {
    setImagePreview(null);
  }, [value]);
  const closeCamera = () => {
    setIsOpenCamera(false);
    onCameraClose();
  };
  const onButtonClick = () => {
    setIsOpenCamera(true);
    onCameraOpen();
  };
  const onSelect = (data) => {
    onImageSelect(data);
    closeCamera();
  };
  return (
    <>
      <DocumentPreview
        add_another={true}
        url={imagePreview}
        setImagePreview={setImagePreview}
        reUpload={onButtonClick}
        documentName={title}
        addAnotherDisable={addAnotherDisable}
        title={title}
        downloadBtnDisabled={downloadBtnDisabled}
      />
      <div>
        {title && (
          <h3 className="text-[13px] font-publicSans text-[#4B465C]  flex mb-[5px]">
            {title} {mandatory && <SvgIcon size={10} type="star" />}
          </h3>
        )}
        {!fieldContainsValue(value) && (
          <HtmlButton
            type={buttonType}
            text={text}
            svgType={svgType}
            onClick={onButtonClick}
            disabled={disabled}
            disabledMessage={disabledMessage}
          />
        )}
        {fieldContainsValue(value) && (
          <HtmlButton
            text={docViewButtonText}
            svgType={docViewButtonSvgType}
            type={docViewButtonType}
            svgSize={docViewButtonSvgSize}
            style={{
              color: "rgb(52, 199, 89)",
              borderColor: "rgb(52, 199, 89)",
              ...docViewButtonStyle,
            }}
            onClick={() => {
              setImagePreview(value);
            }}
            className={""}
            disabled={viewButtonDisabled}
          />
        )}
        <MultiImageCaptureCamera
          open={isCameraOpen}
          onClose={closeCamera}
          inputFileType={fileType}
          multiple={allowMultiple}
          maxSelect={numberOfImagesAllowed}
          maxFileSize={maxFileSize}
          note={note}
          onSelect={onSelect}
        />
      </div>
    </>
  );
};

export { WebCameraModal, WebCamera, MultiImageCaptureCamera, DocumentUploadHandler };
